import {
  getMedicalTreatmentRecordStatusClassName,
  getMedicalTreatmentRecordStatusLabel,
} from "@/components/features/matter/medical-treatments/AppointmentRecordStatusIcon";
import { MatterTreatmentBillContextMenu } from "@/components/features/matter/context-menus/MatterTreatmentBillContextMenu";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/shadcn/Dialog/Dialog";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { Typography } from "@/components/ui/Typography/Typography";
import { useUser } from "@/components/UserProvider";
import type { MedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMedicalTreatmentBill } from "@/lib/data/react-query/matters/medical-treatment-bill";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDate, trimText } from "@/lib/utils/format";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { Eye, FileText, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";

interface MatterTreatmentBillsCardProps {
  bills: MedicalTreatmentBillViewModel[];
  matter: Matter;
}

export function MatterTreatmentBillsCard({
  bills,
  matter,
}: MatterTreatmentBillsCardProps) {
  const { hasActionPermission } = useUser();
  const { openTreatmentBillModal } = useGlobalModal();
  const deleteBill = useDeleteMedicalTreatmentBill();
  const [previewBillId, setPreviewBillId] = useState<string | null>(null);
  const canEditBills = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteBills = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill,
    ACTION_PERMISSION_VALUES.delete,
  );
  const selectedPreviewBill = useMemo(
    () => bills.find((bill) => bill.id === previewBillId) ?? null,
    [bills, previewBillId],
  );
  const previewBillFiles = selectedPreviewBill
    ? mergeFilesAndReferenceId(
        matter,
        "medicalTreatmentBillId",
        selectedPreviewBill.id,
      )
    : [];

  const openEditBill = (bill: MedicalTreatmentBillViewModel) => {
    if (!canEditBills) return;

    openTreatmentBillModal({
      matterId: matter.id,
      billId: bill.id,
      treatmentId: bill.medicalTreatmentId,
      editing: true,
      initialTab: "details",
    });
  };

  const isAiGeneratedBill = (bill: MedicalTreatmentBillViewModel) =>
    Boolean(bill.aiStatus || bill.aiAffectedFields?.length);

  return (
    <>
      <div>
        {bills.length === 0 ? (
          <div className="bg-card m-4 rounded-lg border border-dashed p-4 text-center">
            <Typography variant="subHeading" size="sm">
              No bills
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/50 text-muted-foreground border-b text-xs font-medium">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Bill name</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Requested</th>
                  <th className="px-4 py-2 text-left font-medium">Received</th>
                  <th className="px-4 py-2 text-right font-medium">
                    Original bill
                  </th>
                  <th className="px-4 py-2 text-right font-medium">Paid</th>
                  <th className="px-4 py-2 text-right font-medium">
                    Outstanding
                  </th>
                  <th className="w-12 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => {
                  const { totalPaid, totalOutstanding } =
                    calculateMedicalTreatmentBillTotals([bill]);
                  const status = bill.recordStatus;
                  const originalBill = bill.originalBill ?? 0;

                  const label = trimText(bill.recordLabel) || "-";

                  return (
                    <MatterTreatmentBillContextMenu
                      key={bill.id}
                      canDelete={canDeleteBills}
                      id={bill.id}
                      isDeleting={deleteBill.isPending}
                      label={label}
                      matter={matter}
                      onDelete={async () => {
                        await deleteBill.mutateAsync({
                          matterId: matter.id,
                          id: bill.id,
                        });
                      }}
                      onEdit={() => {
                        openEditBill(bill);
                      }}
                      originalBill={bill.originalBill}
                      status={status}
                      trigger={
                        <tr
                          className={cn(
                            "border-b last:border-b-0",
                            canEditBills && "hover:bg-muted/50 cursor-pointer",
                          )}
                          onClick={() => {
                            openEditBill(bill);
                          }}
                        >
                          <td className="text-foreground px-4 py-3 font-medium">
                            <div className="flex items-center gap-2">
                              <span>{label}</span>
                              {isAiGeneratedBill(bill) ? (
                                <TooltipWrapper message="AI generated bill">
                                  <span
                                    className="inline-flex"
                                    aria-label="AI generated bill"
                                  >
                                    <Sparkles className="h-4 w-4 text-blue-500" />
                                  </span>
                                </TooltipWrapper>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 text-xs font-medium",
                                getMedicalTreatmentRecordStatusClassName(
                                  status,
                                ),
                              )}
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-current" />
                              {getMedicalTreatmentRecordStatusLabel(status)}
                            </span>
                          </td>
                          <td className="text-muted-foreground px-4 py-3">
                            {bill.recordRequestDate
                              ? formatDate(bill.recordRequestDate)
                              : "-"}
                          </td>
                          <td className="text-muted-foreground px-4 py-3">
                            {bill.recordReceivedDate
                              ? formatDate(bill.recordReceivedDate)
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            <CurrencyWrapper value={originalBill} />
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            <CurrencyWrapper value={totalPaid} />
                          </td>
                          <td
                            className={cn(
                              "px-4 py-3 text-right font-medium",
                              totalOutstanding === 0 && "text-muted-foreground",
                            )}
                          >
                            <CurrencyWrapper value={totalOutstanding} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPreviewBillId(bill.id);
                              }}
                              data-testid="preview-treatment-bill-button"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      }
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog
        open={Boolean(selectedPreviewBill)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setPreviewBillId(null);
          }
        }}
      >
        <DialogContent className="flex h-[90vh] max-h-[90vh] w-[92vw] max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <div className="border-border border-b px-5 py-4">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-muted-foreground h-4 w-4" />
              Bill preview
            </DialogTitle>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {previewBillFiles.length > 0 ? (
              <div className="space-y-4">
                {previewBillFiles.map((file) => (
                  <FileViewer
                    key={file.fileReferenceId}
                    fileType="matter-file"
                    file={{
                      fileName: file.name,
                      presignedURLKey: file.presignedUrlKey || "",
                    }}
                    showTitle
                    compactDocxPreview
                    wrapperClassName="h-[calc(90vh-130px)] min-h-0"
                    viewerClassName="min-h-0"
                    imageClassName="max-h-full"
                  />
                ))}
              </div>
            ) : (
              <div className="border-border text-muted-foreground flex h-[calc(90vh-130px)] min-h-0 items-center justify-center rounded-md border border-dashed p-8 text-center text-sm">
                No file is referenced on this bill.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
